import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Lock, Mail } from 'lucide-react';
import { useAppDispatch } from '@/app/hooks';
import { credentialsReceived } from '@/features/auth/authSlice';
import { organizationSelected } from '@/features/tenant/tenantSlice';
import { useLoginMutation } from '@/api/endpoints';
import { apiErrorMessage, apiFieldErrors } from '@/api/baseApi';
import { Alert, Button, Input } from '@/components/ui';
import { DevCredentials } from '@/components/DevCredentials';
import { AuthLayout } from './AuthLayout';

export function LoginPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [login, { isLoading, error }] = useLoginMutation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const fieldErrors = apiFieldErrors(error);

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();

    const result = await login({ email, password }).unwrap().catch(() => null);
    if (!result) return;

    dispatch(
      credentialsReceived({
        user: result.user,
        accessToken: result.tokens.accessToken,
        refreshToken: result.tokens.refreshToken,
        organizations: result.organizations,
      }),
    );

    // A user with no business goes straight to onboarding rather than an
    // empty dashboard.
    if (result.organizations.length === 0) {
      navigate('/onboarding', { replace: true });
      return;
    }

    const first = result.organizations[0];
    if (first) dispatch(organizationSelected(first.id));

    const from = (location.state as { from?: string } | null)?.from;
    navigate(from ?? '/app/dashboard', { replace: true });
  };

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to pick up where you left off."
      footer={
        <>
          New here?{' '}
          <Link to="/auth/register" className="font-medium text-brand-600 hover:underline">
            Create an account
          </Link>
        </>
      }
    >
      <div className="space-y-5">
        <DevCredentials
          disabled={isLoading}
          onUse={({ email: demoEmail, password: demoPassword }) => {
            setEmail(demoEmail);
            setPassword(demoPassword);
          }}
        />

        <form onSubmit={onSubmit} className="space-y-5" noValidate>
          {error && <Alert tone="danger">{apiErrorMessage(error, 'Sign in failed')}</Alert>}

          <Input
            label="Email"
            type="email"
            autoComplete="email"
            icon={Mail}
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            error={fieldErrors.email}
            placeholder="you@business.com"
          />

          <div>
            <div className="relative">
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                icon={Lock}
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                error={fieldErrors.password}
                className="pr-11"
              />
              {/* A password typed on a phone at a counter is mistyped more
                  often than it is shoulder-surfed. */}
              <button
                type="button"
                onClick={() => setShowPassword((shown) => !shown)}
                className="absolute right-1 top-7 rounded-lg p-2 text-muted hover:text-primary"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
              >
                {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
            <div className="mt-1.5 text-right">
              <Link to="/auth/forgot" className="text-sm text-brand-600 hover:underline">
                Forgot your password?
              </Link>
            </div>
          </div>

          <Button type="submit" size="lg" fullWidth loading={isLoading}>
            Sign in
          </Button>
        </form>
      </div>
    </AuthLayout>
  );
}
